import { useQuery } from 'react-query';
import { useAxiosClient } from '../utils/useAxiosClient';
import { useFilterContext } from '../context/FilterContext';

interface Post {
  date: string;
  brand: string;
  platform: string;
  sentiment: number;
  engagement: number;
  post: string;
  keywordMatches: string[];
  url: string;
}

export interface TopicsData {
  brand_share_of_voice: Array<{
    Brand: string;
    post_count: number;
  }>;
  brand_share_of_engagement: Array<{
    Brand: string;
    post_engscore: number;
  }>;
  brand_average_engagement_per_post: Array<{
    Brand: string;
    total_engagement: number;
    post_count: number;
  }>;
  platform_posts_breakdown: Array<{
    Platform: string;
    Brand: string;
    post_count: number;
  }>;
  platform_engagement_breakdown: Array<{
    Platform: string;
    Brand: string;
    total_engscore: number;
    count: number;
  }>;
  platform_average_engagement_per_post: Array<{
    Platform: string;
    Brand: string;
    post_engscore: number;
    post_count: number;
  }>;
  // New trend data payloads
  volume_trend: {
    daily: Array<Record<string, number | string>>;
    weekly: Array<Record<string, number | string>>;
    monthly: Array<Record<string, number | string>>;
  };
  engagement_trend: {
    daily: Array<Record<string, number | string>>;
    weekly: Array<Record<string, number | string>>;
    monthly: Array<Record<string, number | string>>;
  };
  avg_engagement_trend: {
    daily: Array<Record<string, number | string>>;
    weekly: Array<Record<string, number | string>>;
    monthly: Array<Record<string, number | string>>;
  };
}

export const useTopicsData = (
  orgId: number, 
  selectedPillars: string[] = [],
  startDate: string | null = null,
  endDate: string | null = null
) => {
  const axiosClient = useAxiosClient();
  const { availablePillars, isInitialized } = useFilterContext();
  
  const { data, isLoading, error } = useQuery(
    // Include availablePillars and isInitialized in the query key to re-trigger query when they change
    ['topicsData', orgId, selectedPillars, startDate, endDate, availablePillars, isInitialized],
    async () => {
      if (!orgId || !isInitialized) { // Ensure context is ready
        throw new Error('Organization ID or FilterContext not initialized');
      }

      const params = new URLSearchParams();
      params.append('org', orgId.toString());
      
      if (selectedPillars.length > 0) {
        // Robust check if all available pillars are selected
        const allPillarsActuallySelected = Array.isArray(availablePillars) &&
                                         availablePillars.length > 0 &&
                                         selectedPillars.length === availablePillars.length &&
                                         selectedPillars.every(p => availablePillars.includes(p)) &&
                                         availablePillars.every(p => selectedPillars.includes(p));

        if (!allPillarsActuallySelected) {
          selectedPillars.forEach(pillar => {
            if (pillar) { // Ensure pillar is not null/empty
              params.append('pillars', pillar);
            }
          });
        }
      }

      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }
      
      const response = await axiosClient.get<TopicsData>(`/api/get-topics-data?${params.toString()}`);
      
      if ('error' in response.data && response.data.error) {
        throw new Error(typeof response.data.error === 'string' ? response.data.error : 'An error occurred while fetching topics data');
      }
      
      return response.data;
    },
    {
      enabled: !!orgId && isInitialized, // Query enabled only if orgId and FilterContext are initialized
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: 1,
    }
  );

  return { 
    data, 
    loading: isLoading, 
    error: error ? (error instanceof Error ? error.message : 'An unknown error occurred') : null 
  };
}; 